import * as React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TAB_LABEL_KEY, visibleModelTabs, type ModelTab } from '../../views/model/modelTabs';
import { TAB_ICON } from './navItems';
import { RailRow } from './RailRow';
import { RAIL } from './railTokens';

/** The model the caller is inside, as the shell knows it. `name` is null until the record lands. */
export interface RailModelContext {
  name: string | null;
  tab: ModelTab;
  onTab: (tab: ModelTab) => void;
  /** True inside a project under this model: the active link then still navigates, back to that tab. */
  withinProject?: boolean;
}

const { modelLinks: M } = RAIL;

/**
 * The rail's model level, shown only inside a model: the model's name, and one 40px link
 * per model tab, with a 1px thread beside the links and one gold segment that slides to the active
 * link. The active link has no fill; it rests on the segment and on its text weight.
 *
 * The group opens downward under the Models row and leaving reverses it. `context` going undefined is
 * what closes it, so the group keeps the LAST model it was shown for until the close finishes. It is
 * mounted closed from the first render, so the very first entry animates like every later one. While
 * closed the links are `visibility: hidden`, which keeps them out of the tab order without relying on
 * `transitionend` (never fired under the global reduced-motion rule).
 *
 * The name is shown only while the rail is open; collapsed, the links' tooltips name each section.
 * The links are the rail's `role=button` rows; the page keeps the only `tablist`. Both read the same
 * URL-derived tab, so the active link and the active page tab cannot disagree.
 */
export function RailModelLinks(props: { context: RailModelContext | undefined; open: boolean }) {
  const { open } = props;
  const { t } = useTranslation();
  const lastRef = React.useRef(props.context);
  if (props.context) lastRef.current = props.context;
  const ctx = props.context ?? lastRef.current;

  const inModel = props.context !== undefined;
  const tabs = visibleModelTabs();
  const activeIndex = Math.max(0, ctx ? tabs.indexOf(ctx.tab) : 0);
  // No placeholder name: a stand-in would flash to the real one.
  const name = ctx?.name ?? '';
  const ease = RAIL.easing.emphasised;

  return (
    <Box
      data-testid="rail-model-links"
      sx={{
        flex: 'none',
        overflow: 'hidden',
        boxSizing: 'border-box',
        mt: inModel ? '8px' : 0,
        maxHeight: inModel ? 380 : 0,
        opacity: inModel ? 1 : 0,
        transform: inModel ? 'translateY(0)' : 'translateY(-4px)',
        visibility: inModel ? 'visible' : 'hidden',
        transition: [
          `max-height ${M.openMs}ms ${ease}`,
          `margin ${M.openMs}ms ${ease}`,
          'opacity 0.3s ease',
          `transform ${M.openMs}ms ${ease}`,
          `visibility 0s linear ${inModel ? 0 : M.openMs}ms`,
        ].join(', '),
      }}
    >
      <Box aria-hidden sx={{ height: 0, m: '0 0 6px' }} />
      <Typography
        noWrap
        data-testid="rail-model-name"
        title={name || undefined}
        sx={{
          fontSize: 12,
          fontWeight: 500,
          letterSpacing: '0.3px',
          lineHeight: 'normal',
          color: M.nameColor,
          maxHeight: open ? 24 : 0,
          mb: open ? '8px' : 0,
          opacity: open ? 1 : 0,
          transition: `max-height ${M.captionMs}ms ${ease}, margin ${M.captionMs}ms ${ease}, opacity 0.18s ease`,
        }}
      >
        {name}
      </Typography>
      <Box
        role="group"
        aria-label={name || t('nav:rail.modelLinksLabel')}
        sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: `${M.linkPitch - M.linkHeight}px` }}
      >
        <Box aria-hidden sx={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: '1px', background: M.thread }} />
        <Box
          data-testid="rail-model-marker"
          aria-hidden
          sx={{
            position: 'absolute',
            left: '-1px',
            top: activeIndex * M.linkPitch + (M.linkHeight - M.segmentHeight) / 2,
            width: 3,
            height: M.segmentHeight,
            borderRadius: '3px',
            background: 'var(--hg)',
            pointerEvents: 'none',
            opacity: inModel ? 1 : 0,
            transition: `top 0.4s ${ease}`,
          }}
        />
        {tabs.map((id) => {
          const Icon = TAB_ICON[id];
          return (
            <RailRow
              key={id}
              variant="model"
              icon={<Icon />}
              label={t(TAB_LABEL_KEY[id])}
              open={open}
              active={ctx?.tab === id}
              onActivate={() => { if (ctx && (ctx.tab !== id || ctx.withinProject)) ctx.onTab(id); }}
            />
          );
        })}
      </Box>
    </Box>
  );
}
